/* eslint-disable @typescript-eslint/no-explicit-any */
import { AuthModel } from "./_models";

/** @deprecated Tokens are in-memory / sessionStorage (ENG-4781). Kept for migrations. */
const AUTH_LOCAL_STORAGE_KEY = "stream-auth-token";
const ACCESS_TOKEN_KEY = "access_token";
const ACCESS_TOKEN_EXPIRY_KEY = "access_token_expiry";
/**
 * Tab-scoped JWT mirror — survives Cmd+R, dies when the tab closes.
 * Never written to localStorage (ENG-4781). HttpOnly refresh cookie still
 * covers brand-new tabs when logout intent is not set.
 */
const SESSION_AUTH_KEY = "stream-auth-session";
/**
 * Durable (non-secret) marker: user explicitly signed out.
 * AuthInit must not rebuild from the HttpOnly refresh cookie while this is set.
 * Cleared on the next successful setAuth / login.
 */
const LOGOUT_INTENT_KEY = "stream-auth-logout-intent";
const AUTH_SYNC_CHANNEL = "stream-merchant-auth";

/** In-memory session — hydrated from sessionStorage on cold start within the tab. */
let memoryAuth: AuthModel | undefined;
let memoryAccessTokenExpiry: string | undefined;

const TOKEN_STORAGE_KEYS = [
  AUTH_LOCAL_STORAGE_KEY,
  ACCESS_TOKEN_KEY,
  ACCESS_TOKEN_EXPIRY_KEY,
] as const;

type SessionAuthPayload = {
  auth: AuthModel;
  expiry?: string;
};

type AuthListener = (auth: AuthModel | undefined) => void;
const listeners = new Set<AuthListener>();

const notifyListeners = (): void => {
  listeners.forEach((listener) => {
    try {
      listener(memoryAuth);
    } catch {
      // ignore listener errors
    }
  });
};

/** Subscribe to in-memory / cross-tab auth changes (for React providers). */
const subscribeAuth = (listener: AuthListener): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

type AuthSyncMessage =
  | { type: "auth-set"; auth: AuthModel; expiry?: string }
  | { type: "auth-clear" }
  | { type: "auth-request" };

const AUTH_REQUEST_TIMEOUT_MS = 300;

let syncChannel: BroadcastChannel | null = null;

const clearSessionAuth = (): void => {
  if (typeof sessionStorage === "undefined") {
    return;
  }
  try {
    sessionStorage.removeItem(SESSION_AUTH_KEY);
  } catch (error) {
    console.error("AUTH SESSION STORAGE CLEAR ERROR", error);
  }
};

const writeSessionAuth = (): void => {
  if (typeof sessionStorage === "undefined") {
    return;
  }
  try {
    if (!memoryAuth) {
      sessionStorage.removeItem(SESSION_AUTH_KEY);
      return;
    }
    const payload: SessionAuthPayload = {
      auth: memoryAuth,
      expiry: memoryAccessTokenExpiry,
    };
    sessionStorage.setItem(SESSION_AUTH_KEY, JSON.stringify(payload));
  } catch (error) {
    console.error("AUTH SESSION STORAGE WRITE ERROR", error);
  }
};

/** Pull tab-scoped JWT back into memory after Cmd+R (same tab only). */
const hydrateFromSessionStorage = (): void => {
  if (memoryAuth || typeof sessionStorage === "undefined") {
    return;
  }
  try {
    const raw = sessionStorage.getItem(SESSION_AUTH_KEY);
    if (!raw) {
      return;
    }
    const parsed = JSON.parse(raw) as SessionAuthPayload;
    if (parsed?.auth?.access_token) {
      memoryAuth = parsed.auth;
      memoryAccessTokenExpiry = parsed.expiry;
    } else {
      sessionStorage.removeItem(SESSION_AUTH_KEY);
    }
  } catch (error) {
    console.error("AUTH SESSION STORAGE HYDRATE ERROR", error);
    clearSessionAuth();
  }
};

const getSyncChannel = (): BroadcastChannel | null => {
  if (typeof BroadcastChannel === "undefined") {
    return null;
  }
  if (!syncChannel) {
    syncChannel = new BroadcastChannel(AUTH_SYNC_CHANNEL);
    syncChannel.onmessage = (event: MessageEvent<AuthSyncMessage>) => {
      const data = event.data;
      if (!data || typeof data !== "object") {
        return;
      }
      if (data.type === "auth-set") {
        memoryAuth = data.auth;
        memoryAccessTokenExpiry = data.expiry;
        writeSessionAuth();
        clearLogoutIntent();
        clearLegacyTokenStorage();
        notifyListeners();
      } else if (data.type === "auth-clear") {
        memoryAuth = undefined;
        memoryAccessTokenExpiry = undefined;
        clearSessionAuth();
        // Originating tab already wrote the intent key into shared localStorage.
        markLogoutIntent();
        notifyListeners();
      } else if (data.type === "auth-request" && memoryAuth) {
        // Another tab just opened — hand it our live session (sessionStorage
        // is tab-scoped, so Cmd+R works but new tabs need this).
        syncChannel?.postMessage({
          type: "auth-set",
          auth: memoryAuth,
          expiry: memoryAccessTokenExpiry,
        } satisfies AuthSyncMessage);
      }
    };
  }
  return syncChannel;
};

const broadcast = (message: AuthSyncMessage): void => {
  try {
    getSyncChannel()?.postMessage(message);
  } catch {
    // BroadcastChannel unavailable / closed
  }
};

/** Ask sibling tabs for an existing session (new-tab UX). */
const requestAuthFromOtherTabs = (): void => {
  broadcast({ type: "auth-request" });
};

/**
 * Wait briefly for a sibling tab to share its JWT via BroadcastChannel.
 * Resolves with auth if received (or already present); otherwise undefined.
 */
const waitForAuthFromOtherTabs = (): Promise<AuthModel | undefined> => {
  hydrateFromSessionStorage();
  if (memoryAuth?.access_token) {
    return Promise.resolve(memoryAuth);
  }

  return new Promise((resolve) => {
    let settled = false;
    const finish = (auth: AuthModel | undefined) => {
      if (settled) {
        return;
      }
      settled = true;
      window.clearTimeout(timeoutId);
      unsubscribe();
      resolve(auth);
    };

    const unsubscribe = subscribeAuth((next) => {
      if (next?.access_token) {
        finish(next);
      }
    });

    const timeoutId = window.setTimeout(() => {
      finish(getAuth());
    }, AUTH_REQUEST_TIMEOUT_MS);

    requestAuthFromOtherTabs();
  });
};

const markLogoutIntent = (): void => {
  if (typeof localStorage === "undefined") {
    return;
  }
  try {
    localStorage.setItem(LOGOUT_INTENT_KEY, "1");
  } catch (error) {
    console.error("AUTH LOGOUT INTENT SET ERROR", error);
  }
};

const clearLogoutIntent = (): void => {
  if (typeof localStorage === "undefined") {
    return;
  }
  try {
    localStorage.removeItem(LOGOUT_INTENT_KEY);
  } catch (error) {
    console.error("AUTH LOGOUT INTENT CLEAR ERROR", error);
  }
};

/** True when the merchant explicitly signed out and refresh must not restore. */
const hasLogoutIntent = (): boolean => {
  if (typeof localStorage === "undefined") {
    return false;
  }
  try {
    return localStorage.getItem(LOGOUT_INTENT_KEY) === "1";
  } catch {
    return false;
  }
};

/** Drop legacy JS-readable token copies left from older builds. */
const clearLegacyTokenStorage = (): void => {
  if (typeof localStorage === "undefined") {
    return;
  }
  try {
    TOKEN_STORAGE_KEYS.forEach((key) => localStorage.removeItem(key));
  } catch (error) {
    console.error("AUTH LEGACY STORAGE CLEAR ERROR", error);
  }
};

const getAuth = (): AuthModel | undefined => {
  hydrateFromSessionStorage();
  return memoryAuth;
};

const getAccessTokenExpiry = (): string | undefined => {
  hydrateFromSessionStorage();
  return memoryAccessTokenExpiry;
};

const setAccessTokenExpiry = (expiry: string | undefined): void => {
  memoryAccessTokenExpiry = expiry;
  if (memoryAuth) {
    writeSessionAuth();
  }
};

const setAuth = (auth: AuthModel) => {
  memoryAuth = auth;
  writeSessionAuth();
  // Ensure older builds' localStorage copies cannot outlive a new session.
  clearLegacyTokenStorage();
  clearLogoutIntent();
  notifyListeners();
  broadcast({
    type: "auth-set",
    auth,
    expiry: memoryAccessTokenExpiry,
  });
};

/**
 * Drop in-memory / session JWT without marking logout intent / broadcasting.
 * Used by login form resets — must not block the next cold-start cookie restore.
 */
const clearAuthMemory = (): void => {
  memoryAuth = undefined;
  memoryAccessTokenExpiry = undefined;
  clearSessionAuth();
  notifyListeners();
};

const removeAuth = () => {
  memoryAuth = undefined;
  memoryAccessTokenExpiry = undefined;
  clearSessionAuth();

  if (typeof localStorage === "undefined") {
    markLogoutIntent();
    notifyListeners();
    broadcast({ type: "auth-clear" });
    return;
  }

  try {
    // stream_onboarding_setup_steps is preserved so the client-only "product"
    // step flag (no backend source of truth) survives logout — kyb/contract
    // on it get re-reconciled against the backend on next load anyway.
    const preserveKeys = [
      "i18nConfig",
      "direction",
      "stream_onboarding_setup_steps",
      // Per-user map: once /onboarding-intro was shown, skip it after re-login.
      "stream_onboarding_intro_seen",
      // Per-user map: notification read state (backend has no read flag).
      "stream_notifications_read",
    ];
    const preserved: Record<string, string> = {};

    preserveKeys.forEach((key) => {
      const value = localStorage.getItem(key);
      if (value !== null) {
        preserved[key] = value;
      }
    });

    localStorage.clear();

    Object.entries(preserved).forEach(([key, value]) => {
      localStorage.setItem(key, value);
    });
  } catch (error) {
    console.error("AUTH LOCAL STORAGE CLEAR ERROR", error);
  }

  // After localStorage.clear — keep intentional logout across reloads.
  markLogoutIntent();
  notifyListeners();
  broadcast({ type: "auth-clear" });
};

/** Test helper — reset module state between Jest cases. */
const resetAuthMemoryForTests = (): void => {
  memoryAuth = undefined;
  memoryAccessTokenExpiry = undefined;
  clearSessionAuth();
  clearLogoutIntent();
  try {
    syncChannel?.close();
  } catch {
    // ignore
  }
  syncChannel = null;
};

export function setupAxios(axios: any) {
  axios.defaults.headers.Accept = "application/json";
  axios.interceptors.request.use(
    (config: { headers: { Authorization: string } }) => {
      const auth = getAuth();
      if (auth && auth.access_token) {
        config.headers.Authorization = `Bearer ${auth.access_token}`;
      }

      return config;
    },
    (err: any) => Promise.reject(err)
  );
}

// Hydrate tab session before anything reads getAuth() (Cmd+R).
hydrateFromSessionStorage();
// Strip any tokens left in localStorage from older releases.
clearLegacyTokenStorage();
// Open the sync channel so this tab receives logout/login from siblings.
getSyncChannel();

export {
  getAuth,
  setAuth,
  removeAuth,
  clearAuthMemory,
  getAccessTokenExpiry,
  setAccessTokenExpiry,
  clearLegacyTokenStorage,
  resetAuthMemoryForTests,
  subscribeAuth,
  requestAuthFromOtherTabs,
  waitForAuthFromOtherTabs,
  hasLogoutIntent,
  markLogoutIntent,
  clearLogoutIntent,
  AUTH_LOCAL_STORAGE_KEY,
  LOGOUT_INTENT_KEY,
  SESSION_AUTH_KEY,
};
